import _objectSpread from '@babel/runtime/helpers/esm/objectSpread2';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import composeRefs from '@seznam/compose-react-refs';
import { useReducer, useRef, useEffect, useMemo, useCallback } from 'react';
import useEffectOnce from 'react-use/lib/useEffectOnce';
import { isNumber, within, uniqueBy, take, isUndefined, keys, isEmptyArray, object, last, assertGet, uniqueArray, omit, isArray, isObject, isString, range, clamp, debounce, includes, isNullOrUndefined } from '@remirror/core-helpers';
import { useId } from '@reach/auto-id';
import { setStatus } from 'a11y-status';
import useShallowCompareEffect from 'react-use/lib/useShallowCompareEffect';
import _createForOfIteratorHelper from '@babel/runtime/helpers/esm/createForOfIteratorHelper';
import computeScrollIntoView from 'compute-scroll-into-view';
import warning from 'tiny-warning';
import { keyName } from 'w3c-keyname';

var SPECIAL_KEYS = ['Tab', 'Space', 'Enter', 'Escape', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'PageUp', 'PageDown', 'SelectAll'];
var SPECIAL_INPUT_KEYS = ['ArrowDown', 'ArrowUp', 'Enter', 'Escape'];
var SPECIAL_MENU_KEYS = ['ArrowDown', 'ArrowUp', 'Space', 'Tab', 'Enter', 'Escape', 'Home', 'End', 'SelectAll'];
var SPECIAL_TOGGLE_BUTTON_KEYS = ['ArrowDown', 'ArrowUp', 'Space'];
var Type = {
  /**
   * Describes a selection only drop down. There is no input for filtering
   * longer lists.
   */
  Select: 'select',

  /**
   * Describes the combination of a selection drop down with an input for
   * filtering potential options.
   */
  ComboBox: 'combobox',

  /**
   * A menu rendered without a toggleButton, combobox or input element. It is up
   * to you to provide the input value and manage the focus.
   */
  ControlledMenu: 'controlled-menu'
};
var MultishiftActionTypes = {
  SelectItems: '$$__SELECT_ITEMS__',
  SelectItem: '$$__SELECT_ITEM__',
  RemoveSelectedItems: '$$_REMOVE__SELECTED_ITEMS__',
  RemoveSelectedItem: '$$__REMOVE_SELECTED_ITEM__',
  ClearSelection: '$$__CLEAR_SELECTION__',
  SetHoverItemIndex: '$$__SET_HOVER_ITEM_INDEX__',
  ToggleMenu: '$$__TOGGLE_MENU__',
  CloseMenu: '$$__CLOSE_MENU__',
  OpenMenu: '$$__OPEN_MENU__',
  SetHighlightedIndexes: '$$__SET_HIGHLIGHTED_INDEXES__',
  SetHighlightedIndex: '$$__SET_HIGHLIGHTED_INDEX__',
  ClearHighlighted: '$$__CLEAR_HIGHLIGHTED__',
  ClearHover: '$$__CLEAR_HOVER__',
  Reset: '$$__RESET__',
  SetState: '$$__SET_STATE__',
  ItemMouseMove: '$$__ITEM_MOUSE_MOVE__',
  ItemMouseLeave: '$$__ITEM_MOUSE_LEAVE__',
  ItemClick: '$$__ITEM_CLICK__',
  ToggleButtonClick: '$$__TOGGLE_BUTTON_CLICK__',
  ToggleButtonBlur: '$$__TOGGLE_BUTTON_BLUR__',
  ToggleButtonSpecialKeyDown: '$$__TOGGLE_BUTTON_SPECIAL_KEY_DOWN__',
  MenuBlur: '$$__MENU_BLUR__',
  MenuSpecialKeyDown: '$$__MENU_SPECIAL_KEY_DOWN__',
  MenuCharacterKeyDown: '$$__MENU_CHARACTER_KEY_DOWN__',
  InputBlur: '$$__INPUT_BLUR__',
  InputSpecialKeyDown: '$$__INPUT_SPECIAL_KEY_DOWN__',
  ClearJumpText: '$$__CLEAR_JUMP_TEXT__',
  InputValueChange: '$$__INPUT_VALUE_CHANGE__',
  ClearInputValue: '$$__CLEAR_INPUT_VALUE__',
  OuterMouseUp: '$$__OUTER_MOUSE_UP__',
  OuterTouchEnd: '$$__OUTER_TOUCH_END__'
};

/* eslint-disable @typescript-eslint/explicit-module-boundary-types */

/**
 * Select the provided items.
 */
function selectItems(items) {
  var keepHighlights = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  return {
    type: MultishiftActionTypes.SelectItems,
    payload: {
      items,
      keepHighlights
    }
  };
}
/**
 * Select the provided item.
 */


function selectItem(item) {
  var keepHighlights = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  return {
    type: MultishiftActionTypes.SelectItem,
    payload: {
      items: [item],
      keepHighlights
    }
  };
}
/**
 * Remove the provided items from the current selection.
 */


function removeSelectedItems(items) {
  var keepHighlights = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  return {
    type: MultishiftActionTypes.RemoveSelectedItems,
    payload: {
      items,
      keepHighlights
    }
  };
}
/**
 * Remove the provided item from the current selection.
 */


function removeSelectedItem(item) {
  var keepHighlights = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  return {
    type: MultishiftActionTypes.RemoveSelectedItem,
    payload: {
      items: [item],
      keepHighlights
    }
  };
}
/**
 * Remove the provided item from the current selection.
 */


function clearSelection() {
  return {
    type: MultishiftActionTypes.ClearSelection
  };
}
/**
 * Set the `hoverIndex` to a certain value.
 */


function setHoverItemIndex(payload) {
  return {
    type: MultishiftActionTypes.SetHoverItemIndex,
    payload
  };
}
/**
 * Toggle the `isOpen` status of the menu.
 */


function toggleMenu() {
  return {
    type: MultishiftActionTypes.ToggleMenu
  };
}
/**
 * Set isOpen to false (closing the menu).
 */


function closeMenu() {
  return {
    type: MultishiftActionTypes.CloseMenu
  };
}
/**
 * Set `isOpen` to true (opening the menu).
 */


function openMenu() {
  return {
    type: MultishiftActionTypes.OpenMenu
  };
}
/**
 * Set the highlighted item indexes.
 */


function setHighlightedIndexes(payload) {
  return {
    type: MultishiftActionTypes.SetHighlightedIndexes,
    payload
  };
}
/**
 * Set the highlighted item index.
 */


function setHighlightedIndex(index) {
  return {
    type: MultishiftActionTypes.SetHighlightedIndex,
    payload: [index]
  };
}
/**
 * Removes all the highlighted items including the hover.
 */


function clearHighlighted() {
  return {
    type: MultishiftActionTypes.ClearHighlighted
  };
}
/**
 * Reset the state of the reducer.
 */


function reset() {
  return {
    type: MultishiftActionTypes.Reset
  };
}
/**
 * Dispatched when the mouse hovers over an item
 */


function itemMouseMove(payload) {
  return {
    type: MultishiftActionTypes.ItemMouseMove,
    payload
  };
}

function itemMouseLeave(payload) {
  return {
    type: MultishiftActionTypes.ItemMouseLeave,
    payload
  };
}
/**
 * Reports when a user has clicked on an item's element.
 */


function itemClick(payload) {
  return {
    type: MultishiftActionTypes.ItemClick,
    payload
  };
}
/**
 * Called when the menu is blurred.
 */


function menuBlur() {
  return {
    type: MultishiftActionTypes.MenuBlur
  };
}

function inputBlur() {
  return {
    type: MultishiftActionTypes.InputBlur
  };
}

function toggleButtonBlur() {
  return {
    type: MultishiftActionTypes.ToggleButtonBlur
  };
}
/**
 * Clears the jump text value.
 */


function clearJumpText() {
  return {
    type: MultishiftActionTypes.ClearJumpText
  };
}

function clearInputValue() {
  return {
    type: MultishiftActionTypes.ClearInputValue
  };
}
/**
 * Dispatches the action for clicking the toggle button
 */


function toggleButtonClick() {
  return {
    type: MultishiftActionTypes.ToggleButtonClick
  };
}

function outerTouchEnd() {
  return {
    type: MultishiftActionTypes.OuterTouchEnd
  };
}

function outerMouseUp() {
  return {
    type: MultishiftActionTypes.OuterMouseUp
  };
}
/**
 * Handle the menu key down event.
 */


function menuSpecialKeyDown(payload) {
  return {
    type: MultishiftActionTypes.MenuSpecialKeyDown,
    payload
  };
}

function toggleButtonSpecialKeyDown(payload) {
  return {
    type: MultishiftActionTypes.ToggleButtonSpecialKeyDown,
    payload
  };
}

function inputSpecialKeyDown(payload) {
  return {
    type: MultishiftActionTypes.InputSpecialKeyDown,
    payload
  };
}

function menuCharacterKeyDown(payload) {
  return {
    type: MultishiftActionTypes.MenuCharacterKeyDown,
    payload
  };
}

function inputValueChange(payload) {
  return {
    type: MultishiftActionTypes.InputValueChange,
    payload
  };
}

function setState(payload) {
  return {
    type: MultishiftActionTypes.SetState,
    payload
  };
}
/* eslint-enable @typescript-eslint/explicit-module-boundary-types */

/**
 * The action creators which can be dispatched via the reducer.
 */


var Actions = {
  itemMouseMove,
  itemMouseLeave,
  itemClick,
  menuBlur,
  toggleButtonBlur,
  inputBlur,
  toggleButtonClick,
  menuSpecialKeyDown,
  toggleButtonSpecialKeyDown,
  inputSpecialKeyDown,
  menuCharacterKeyDown,
  outerTouchEnd,
  outerMouseUp,
  selectItems,
  selectItem,
  removeSelectedItems,
  removeSelectedItem,
  setState,
  clearSelection,
  setHoverItemIndex,
  inputValueChange,
  clearInputValue,
  toggleMenu,
  closeMenu,
  openMenu,
  setHighlightedIndexes,
  setHighlightedIndex,
  clearHighlighted,
  reset,
  clearJumpText
};

var _excluded$2 = ["initialSelectedItems", "initialJumpText", "initialIsOpen", "initialInputValue", "initialHoveredIndex", "initialHighlightedIndexes", "initialHighlightedGroupStartIndex", "initialHighlightedGroupEndIndex", "selectedItems", "jumpText", "isOpen", "inputValue", "hoveredIndex", "highlightedIndexes", "highlightedGroupStartIndex", "highlightedGroupEndIndex"];

/**
 * The default unique identifier getter function.
 */
function defaultGetItemId(item) {
  return item;
}
/**
 * The default itemToString implementation.
 */

function defaultItemToString(item) {
  return item ? String(item) : '';
}
/**
 * The default itemsToString function.
 *
 * Creates a comma separated string of the item string values.
 *
 * @param items - the list of all selected items
 * @param itemToString - retrieve the string from an individual
 */

function defaultItemsToString(selectedItems) {
  var itemToString = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : defaultItemToString;
  return selectedItems.map(itemToString).join(', ');
}
var DEFAULT_STATE = {
  selectedItems: [],
  jumpText: '',
  isOpen: false,
  inputValue: '',
  hoveredIndex: -1,
  highlightedIndexes: [],
  highlightedGroupStartIndex: -1,
  highlightedGroupEndIndex: undefined
};

var noUndefined = (fallback, values) => {
  var _iterator = _createForOfIteratorHelper(values),
      _step;

  try {
    for (_iterator.s(); !(_step = _iterator.n()).done;) {
      var value = _step.value;

      if (!isUndefined(value)) {
        return value;
      }
    }
  } catch (err) {
    _iterator.e(err);
  } finally {
    _iterator.f();
  }

  return fallback;
};
/**
 * Get all the default state values.
 */


function getDefaultState(defaults) {
  var defaultSelectedItems = defaults.defaultSelectedItems,
      defaultJumpText = defaults.defaultJumpText,
      defaultIsOpen = defaults.defaultIsOpen,
      defaultInputValue = defaults.defaultInputValue,
      defaultHoveredIndex = defaults.defaultHoveredIndex,
      defaultHighlightedIndexes = defaults.defaultHighlightedIndexes,
      defaultHighlightedGroupStartIndex = defaults.defaultHighlightedGroupStartIndex,
      defaultHighlightedGroupEndIndex = defaults.defaultHighlightedGroupEndIndex;
  return {
    selectedItems: defaultSelectedItems !== null && defaultSelectedItems !== void 0 ? defaultSelectedItems : DEFAULT_STATE.selectedItems,
    jumpText: noUndefined(DEFAULT_STATE.jumpText, [defaultJumpText]),
    isOpen: noUndefined(DEFAULT_STATE.isOpen, [defaultIsOpen]),
    inputValue: noUndefined(DEFAULT_STATE.inputValue, [defaultInputValue]),
    hoveredIndex: noUndefined(DEFAULT_STATE.hoveredIndex, [defaultHoveredIndex]),
    highlightedIndexes: defaultHighlightedIndexes !== null && defaultHighlightedIndexes !== void 0 ? defaultHighlightedIndexes : DEFAULT_STATE.highlightedIndexes,
    highlightedGroupStartIndex: noUndefined(DEFAULT_STATE.highlightedGroupStartIndex, [defaultHighlightedGroupStartIndex]),
    highlightedGroupEndIndex: noUndefined(DEFAULT_STATE.highlightedGroupEndIndex, [defaultHighlightedGroupEndIndex])
  };
}
/**
 * Get the initial state or props when provided.
 */

function getInitialStateProps(initialProps) {
  var initialSelectedItems = initialProps.initialSelectedItems,
      initialJumpText = initialProps.initialJumpText,
      initialIsOpen = initialProps.initialIsOpen,
      initialInputValue = initialProps.initialInputValue,
      initialHoveredIndex = initialProps.initialHoveredIndex,
      initialHighlightedIndexes = initialProps.initialHighlightedIndexes,
      initialHighlightedGroupStartIndex = initialProps.initialHighlightedGroupStartIndex,
      initialHighlightedGroupEndIndex = initialProps.initialHighlightedGroupEndIndex,
      selectedItems = initialProps.selectedItems,
      jumpText = initialProps.jumpText,
      isOpen = initialProps.isOpen,
      inputValue = initialProps.inputValue,
      hoveredIndex = initialProps.hoveredIndex,
      highlightedIndexes = initialProps.highlightedIndexes,
      highlightedGroupStartIndex = initialProps.highlightedGroupStartIndex,
      highlightedGroupEndIndex = initialProps.highlightedGroupEndIndex,
      props = _objectWithoutProperties(initialProps, _excluded$2);

  var fallback = getDefaultState(props);
  return {
    selectedItems: noUndefined(fallback.selectedItems, [selectedItems, initialSelectedItems]),
    jumpText: noUndefined(fallback.jumpText, [jumpText, initialJumpText]),
    isOpen: noUndefined(fallback.isOpen, [isOpen, initialIsOpen]),
    inputValue: noUndefined(fallback.inputValue, [inputValue, initialInputValue]),
    hoveredIndex: noUndefined(fallback.hoveredIndex, [hoveredIndex, initialHoveredIndex]),
    highlightedIndexes: noUndefined(fallback.highlightedIndexes, [highlightedIndexes, initialHighlightedIndexes]),
    highlightedGroupStartIndex: noUndefined(fallback.highlightedGroupStartIndex, [highlightedGroupStartIndex, initialHighlightedGroupStartIndex]),
    highlightedGroupEndIndex: noUndefined(fallback.highlightedGroupEndIndex, [highlightedGroupEndIndex, initialHighlightedGroupEndIndex])
  };
}

/**
 * The state that corresponds to the default highlight state. Useful when the
 * highlighted values need to be reset.
 */
function getHighlightReset(defaultState) {
  return {
    highlightedGroupEndIndex: defaultState.highlightedGroupEndIndex,
    highlightedGroupStartIndex: defaultState.highlightedGroupStartIndex,
    highlightedIndexes: defaultState.highlightedIndexes,
    hoveredIndex: defaultState.hoveredIndex
  };
}
/**
 * Uses controlled props where available otherwise fallbacks back to internal
 * state.
 */

function getState(state, props) {
  var _props$selectedItems, _props$highlightedInd;

  return {
    selectedItems: (_props$selectedItems = props.selectedItems) !== null && _props$selectedItems !== void 0 ? _props$selectedItems : state.selectedItems,
    jumpText: noUndefined(state.jumpText, [props.jumpText]),
    isOpen: noUndefined(state.isOpen, [props.isOpen]),
    inputValue: noUndefined(state.inputValue, [props.inputValue]),
    hoveredIndex: noUndefined(state.hoveredIndex, [props.hoveredIndex]),
    highlightedIndexes: (_props$highlightedInd = props.highlightedIndexes) !== null && _props$highlightedInd !== void 0 ? _props$highlightedInd : state.highlightedIndexes,
    highlightedGroupStartIndex: noUndefined(state.highlightedGroupStartIndex, [props.highlightedGroupStartIndex]),
    highlightedGroupEndIndex: noUndefined(state.highlightedGroupEndIndex, [props.highlightedGroupEndIndex])
  };
}
var changeHandlerMap = {
  selectedItems: (_ref, _ref2) => {
    var onSelectedItemsChange = _ref.onSelectedItemsChange;
    var state = _ref2.state;
    return onSelectedItemsChange === null || onSelectedItemsChange === void 0 ? void 0 : onSelectedItemsChange(state.selectedItems, state);
  },
  jumpText: (_ref3, _ref4) => {
    var onJumpTextChange = _ref3.onJumpTextChange;
    var state = _ref4.state;
    return onJumpTextChange === null || onJumpTextChange === void 0 ? void 0 : onJumpTextChange(state.jumpText, state);
  },
  isOpen: (_ref5, _ref6) => {
    var onIsOpenChange = _ref5.onIsOpenChange;
    var state = _ref6.state;
    return onIsOpenChange === null || onIsOpenChange === void 0 ? void 0 : onIsOpenChange(state.isOpen, state);
  },
  inputValue: (_ref7, _ref8) => {
    var onInputValueChange = _ref7.onInputValueChange;
    var state = _ref8.state;
    return onInputValueChange === null || onInputValueChange === void 0 ? void 0 : onInputValueChange(state.inputValue, state);
  },
  hoveredIndex: (_ref9, _ref10) => {
    var onHoveredIndexChange = _ref9.onHoveredIndexChange;
    var state = _ref10.state;
    return onHoveredIndexChange === null || onHoveredIndexChange === void 0 ? void 0 : onHoveredIndexChange(state.hoveredIndex, state);
  },
  highlightedIndexes: (_ref11, _ref12) => {
    var onHighlightedIndexesChange = _ref11.onHighlightedIndexesChange;
    var state = _ref12.state;
    return onHighlightedIndexesChange === null || onHighlightedIndexesChange === void 0 ? void 0 : onHighlightedIndexesChange(state.highlightedIndexes, state);
  },
  highlightedGroupStartIndex: (_ref13, _ref14) => {
    var onHighlightedGroupStartIndexChange = _ref13.onHighlightedGroupStartIndexChange;
    var state = _ref14.state;
    return onHighlightedGroupStartIndexChange === null || onHighlightedGroupStartIndexChange === void 0 ? void 0 : onHighlightedGroupStartIndexChange(state.highlightedGroupStartIndex, state);
  },
  highlightedGroupEndIndex: (_ref15, _ref16) => {
    var onHighlightedGroupEndIndexChange = _ref15.onHighlightedGroupEndIndexChange;
    var state = _ref16.state;
    return onHighlightedGroupEndIndexChange === null || onHighlightedGroupEndIndexChange === void 0 ? void 0 : onHighlightedGroupEndIndexChange(state.highlightedGroupEndIndex, state);
  }
};
/**
 * Call all relevant change handlers.
 */

function callChangeHandlers(handlers, changeset) {
  var changes = changeset.changes,
      state = changeset.state,
      prevState = changeset.prevState;
  var changedKeys = keys(changes);
  var onStateChange = handlers.onStateChange;
  changedKeys.forEach(key => {
    changeHandlerMap[key](handlers, {
      changes,
      state,
      prevState
    });
  });

  if (!isEmptyArray(changedKeys.length) && onStateChange) {
    onStateChange(changes, state);
  }
}

/**
 * Get the ids for each element.
 */
function getElementIds(defaultId) {
  var props = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : object();
  var id = props.id,
      labelId = props.labelId,
      menuId = props.menuId,
      getItemA11yId = props.getItemA11yId,
      toggleButtonId = props.toggleButtonId,
      inputId = props.inputId;
  var uniqueId = id === undefined ? "multishift-".concat(defaultId) : id;
  return {
    labelId: labelId !== null && labelId !== void 0 ? labelId : "".concat(uniqueId, "-label"),
    inputId: inputId !== null && inputId !== void 0 ? inputId : "".concat(uniqueId, "-input"),
    menuId: menuId !== null && menuId !== void 0 ? menuId : "".concat(uniqueId, "-menu"),
    getItemA11yId: getItemA11yId !== null && getItemA11yId !== void 0 ? getItemA11yId : index => "".concat(uniqueId, "-item-").concat(index !== null && index !== void 0 ? index : 0),
    toggleButtonId: toggleButtonId !== null && toggleButtonId !== void 0 ? toggleButtonId : "".concat(uniqueId, "-toggle-button")
  };
}
function getNextWrappingIndex(_ref17) {
  var start = _ref17.start,
      steps = _ref17.steps,
      size = _ref17.size,
      circular = _ref17.circular;

  if (size === 0) {
    return;
  }

  if (start === -1) {
    return steps > 0 ? 0 : size - 1;
  }

  var nextIndex = start + steps;

  if (nextIndex < 0) {
    return circular ? size - 1 : 0;
  }

  if (nextIndex >= size) {
    return circular ? 0 : size - 1;
  }

  return nextIndex;
}
/**
 * Check whether the provided value is a valid index.
 */

function isValidIndex(index) {
  return isNumber(index) && index > -1;
}
/**
 * Get the next index when navigating with arrow keys.
 */

function getNextWrappingIndexes(params) {
  var index = getNextWrappingIndex(params);
  return isValidIndex(index) ? [index] : [];
}

/**
 * Finds the nearest match when typing into a non input dropdown.
 */
function getItemIndexesByJumpText(_ref18) {
  var text = _ref18.text,
      highlightedIndexes = _ref18.highlightedIndexes,
      items = _ref18.items,
      _ref18$itemToString = _ref18.itemToString,
      itemToString = _ref18$itemToString === void 0 ? defaultItemToString : _ref18$itemToString;
  var newHighlightedIndex = -1;

  var finder = str => str.startsWith(text);

  var itemStrings = items.map(item => itemToString(item).toLowerCase());
  var startPosition = (Math.min(...highlightedIndexes) || -1) + 1;
  newHighlightedIndex = itemStrings.slice(startPosition).findIndex(finder);

  if (newHighlightedIndex > -1) {
    return [newHighlightedIndex + startPosition];
  }

  var index = itemStrings.slice(0, startPosition).findIndex(finder);
  return isValidIndex(index) ? [index] : [];
}
/**
 * Determines which highlighted indexes should be available on first open.
 */

function getHighlightedIndexOnOpen(props, state, offset, getItemId) {
  var items = props.items,
      initialHighlightedIndexes = props.initialHighlightedIndexes,
      defaultHighlightedIndexes = props.defaultHighlightedIndexes;
  var selectedItems = state.selectedItems,
      highlightedIndexes = state.highlightedIndexes; // initialHighlightedIndexes will give value to highlightedIndex on initial
  // state only.

  if (!isUndefined(initialHighlightedIndexes) && !isEmptyArray(highlightedIndexes)) {
    return initialHighlightedIndexes;
  }

  if (defaultHighlightedIndexes) {
    return defaultHighlightedIndexes;
  }

  if (!isEmptyArray(selectedItems)) {
    var idsOfItems = items.map(getItemId);

    var _index = selectedItems.map(selectedItem => idsOfItems.indexOf(getItemId(selectedItem))).findIndex(isValidIndex);

    if (!isValidIndex(_index)) {
      return [];
    }

    if (offset === 0) {
      return [_index];
    }

    return getNextWrappingIndexes({
      steps: offset,
      start: _index,
      size: items.length,
      circular: false
    });
  }

  if (offset === 0) {
    return [0];
  }

  return offset < 0 ? [items.length - 1] : [0];
}
/**
 * Get the item index from the items prop.
 */

function getItemIndex(index, item, items) {
  if (index !== undefined) {
    return index;
  }

  if (items.length === 0) {
    return -1;
  }

  return items.indexOf(item);
}

/**
 * Get the most recently updated highlighted index.
 *
 * Returns -1 when no highlighted index is found.
 */
function getMostRecentHighlightIndex(lastHighlight) {
  var highlightedGroupEndIndex = lastHighlight.highlightedGroupEndIndex,
      highlightedGroupStartIndex = lastHighlight.highlightedGroupStartIndex,
      highlightedIndexes = lastHighlight.highlightedIndexes;
  var lastIndex = last(highlightedIndexes);
  return isValidIndex(highlightedGroupEndIndex) ? highlightedGroupEndIndex : isValidIndex(highlightedGroupStartIndex) ? highlightedGroupStartIndex : isValidIndex(lastIndex) ? lastIndex : -1;
}
/**
 * Check if the browser is running on a mac.
 */

var isMac = () => /Mac|iPod|iPhone|iPad/.test(navigator.platform);

/**
 * Returns true when all items are selected within the list.
 */
function allItemsSelected(currentItems, newItems, getItemId) {
  return !isEmptyArray(newItems) ? newItems.every(newItem => currentItems.some(item => getItemId(item) === getItemId(newItem))) : false;
}
/**
 * Adds the list of `newItems` to the list of `prevItems`. If `multiple` is
 * false (or undefined) then simply replace the array with the first item from
 * the `newItems` list.
 */

function _addItems(currentItems, newItems, getItemId, multiple) {
  return multiple ? uniqueBy([...currentItems, ...newItems], getItemId, true) : take(newItems, 1);
}

function _removeItems(currentItems, removalItems, getItemId) {
  return currentItems.filter(prevItem => !removalItems.some(newItem => getItemId(newItem) === getItemId(prevItem)));
}
function toggleSelectedItems(currentItems, toggleItems, getItemId, multiple) {
  return allItemsSelected(currentItems, toggleItems, getItemId) ? _removeItems(currentItems, toggleItems, getItemId) : _addItems(currentItems, toggleItems, getItemId, multiple);
}
/**
 * Get an array of all the highlighted items Including any from the currently
 * incomplete group.
 */

function getHighlightedIndexes(_ref19) {
  var start = _ref19.start,
      end = _ref19.end,
      indexes = _ref19.indexes,
      items = _ref19.items,
      hoveredIndex = _ref19.hoveredIndex;
  var max = items.length - 1;
  var groupIndexes = isValidIndex(start) ? range(clamp({
    min: 0,
    max,
    value: start
  }), clamp({
    min: 0,
    max,
    value: isValidIndex(end) ? end : start
  })) : [];
  var hoveredIndexes = isValidIndex(hoveredIndex) ? [hoveredIndex] : [];
  return uniqueArray([...hoveredIndexes, ...indexes, ...groupIndexes], true);
}
/**
 * Checks whether the an index is highlighted within a set of indexes and a
 * highlighted group.
 */

function checkItemHighlighted(index, options) {
  var start = options.start,
      end = options.end,
      indexes = options.indexes;
  return indexes.includes(index) || within(index, start, end);
}
/**
 * Removes any unchanged values from the changes object so that only the correct
 * callbacks are triggered.
 */

function omitUnchangedState(changes, _ref20) {
  var state = _ref20.state,
      getItemId = _ref20.getItemId;
  return omit(changes, (value, key) => {
    if (isArray(value)) {
      if (key === 'selectedItems') {
        return value.length !== state.selectedItems.length || value.some((item, index) => getItemId(item) !== getItemId(assertGet(state.selectedItems, index)));
      }

      if (key === 'highlightedIndexes') {
        return value.length !== state.highlightedIndexes.length || value.some((val, index) => val !== state.highlightedIndexes[index]);
      }
    }

    return value !== state[key];
  });
}
/**
 * Create the desired change object when an item is clicked.
 */

function getChangesFromItemClick(_ref21) {
  var modifiers = _ref21.modifiers,
      items = _ref21.items,
      defaultState = _ref21.defaultState,
      state = _ref21.state,
      index = _ref21.index,
      props = _ref21.props,
      getItemId = _ref21.getItemId;
  var selectedItem = items[index];
  var isOpen = props.multiple ? true : defaultState.isOpen;
  var params = {
    state,
    getItemId
  };
  var defaultReturn = {
    highlightedGroupEndIndex: defaultState.highlightedGroupEndIndex,
    highlightedGroupStartIndex: props.multiple ? index : defaultState.highlightedGroupStartIndex
  };

  if (!selectedItem) {
    // TODO check if this logic is desirable
    return _objectSpread(_objectSpread({}, defaultReturn), {}, {
      isOpen
    });
  }

  var selectedItems = toggleSelectedItems(state.selectedItems, [selectedItem], getItemId, props.multiple); // Check if the modifier for selecting multiple items is pressed.

  var shiftKeyPressed = modifiers.includes('shiftKey'); // Check if the modifier for highlighting an additional item is pressed.

  var singleHighlightKeyPressed = modifiers.includes(isMac() ? 'metaKey' : 'ctrlKey') && modifiers.length === 1;

  if (!props.multiple) {
    return _objectSpread(_objectSpread({}, defaultReturn), {}, {
      highlightedIndexes: defaultState.highlightedIndexes,
      selectedItems
    });
  }

  if (singleHighlightKeyPressed) {
    var indexes = getHighlightedIndexes({
      indexes: state.highlightedIndexes,
      start: state.highlightedGroupStartIndex,
      end: state.highlightedGroupEndIndex,
      hoveredIndex: props.includeHoveredIndexInSelection ? state.hoveredIndex : undefined,
      items
    });
    var isHighlighted = checkItemHighlighted(index, {
      indexes,
      start: state.highlightedGroupStartIndex,
      end: state.highlightedGroupEndIndex
    });
    var extra = isHighlighted ? {
      highlightedIndexes: indexes.filter(ii => ii !== index),
      highlightedGroupEndIndex: undefined,
      highlightedGroupStartIndex: -1
    } : {
      highlightedIndexes: indexes,
      highlightedGroupStartIndex: index
    };

    var changes = _objectSpread(_objectSpread({}, defaultReturn), extra);

    return omitUnchangedState(changes, params);
  }

  if (shiftKeyPressed) {
    var _indexes = uniqueArray(state.highlightedIndexes, true);

    var _extra = isValidIndex(state.highlightedGroupStartIndex) ? {
      highlightedIndexes: _indexes,
      highlightedGroupStartIndex: state.highlightedGroupStartIndex,
      highlightedGroupEndIndex: index
    } : {
      highlightedIndexes: _indexes,
      highlightedGroupStartIndex: index
    };

    var _changes = _objectSpread(_objectSpread({}, defaultReturn), _extra);

    return omitUnchangedState(_changes, params);
  }

  return omitUnchangedState(_objectSpread(_objectSpread({}, defaultReturn), {}, {
    selectedItems,
    isOpen,
    highlightedIndexes: defaultState.highlightedIndexes
  }), params);
}

/**
 * Normalizes the 'key' property of a KeyboardEvent in IE/Edge
 *
 * @param event - the keyboard event
 */
function getKeyName(event) {
  var key = keyName(event.nativeEvent);

  if (key === ' ') {
    return 'Space';
  }

  if (key.toLowerCase() === 'a' && isMac() ? event.metaKey : event.ctrlKey) {
    return 'SelectAll';
  }

  return key;
}
/**
 * Log a warning when using in an internal type that doesn't get resolved.
 */

function warnIfInternalType(type) {
  var message = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '';
  process.env.NODE_ENV !== "production" ? warning(!type.startsWith('$$'), message) : void 0;
}

/**
 * Get the changes that have happened when a menu key is pressed.
 */
function getChangesFromMenuKeyDown(_ref22) {
  var modifiers = _ref22.modifiers,
      defaultState = _ref22.defaultState,
      state = _ref22.state,
      key = _ref22.key,
      items = _ref22.items,
      getItemId = _ref22.getItemId,
      props = _ref22.props,
      disabled = _ref22.disabled;
  // Check if the modifier for selecting multiple items is pressed.
  var shiftKeyPressed = modifiers.includes('shiftKey');
  var metaKeyPressed = modifiers.includes('metaKey'); // Mac only

  var params = {
    state,
    getItemId
  };
  var mostRecentHighlightIndex = getMostRecentHighlightIndex(state);
  var highlightReset = getHighlightReset(defaultState);
  var indexes = getHighlightedIndexes({
    start: state.highlightedGroupStartIndex,
    end: state.highlightedGroupEndIndex,
    indexes: state.highlightedIndexes,
    hoveredIndex: props.includeHoveredIndexInSelection ? state.hoveredIndex : undefined,
    items
  }).filter(index => !disabled.includes(index));

  if (key === 'Escape') {
    return omitUnchangedState(_objectSpread(_objectSpread({}, getHighlightReset(defaultState)), {}, {
      isOpen: false
    }), params);
  }

  if (key === 'Enter' || key === 'Space') {
    var highlightedItems = indexes.map(index => assertGet(items, index));
    var highlights = props.multiple ? {} : _objectSpread(_objectSpread({}, highlightReset), {}, {
      highlightedIndexes: [mostRecentHighlightIndex]
    });
    var selectedItems = toggleSelectedItems(state.selectedItems, highlightedItems, getItemId, props.multiple);

    var changes = _objectSpread(_objectSpread({}, highlights), {}, {
      isOpen: props.multiple ? true : defaultState.isOpen,
      jumpText: defaultState.jumpText,
      selectedItems
    });

    return omitUnchangedState(changes, params);
  }

  if (key === 'SelectAll') {
    // Create a new indexes array with all the selected item and set the
    // starting index to the previous highlighted index so that it retains that
    // for the next render.
    var _changes2 = {
      highlightedIndexes: range(0, items.length - 1).filter(index => !disabled.includes(index)),
      highlightedGroupStartIndex: mostRecentHighlightIndex,
      highlightedGroupEndIndex: defaultState.highlightedGroupEndIndex
    };
    return omitUnchangedState(_changes2, params);
  }

  if (props.multiple && (key === 'ArrowDown' || key === 'ArrowUp') && shiftKeyPressed) {
    var isDown = key === 'ArrowDown';

    var _index2 = getNextWrappingIndex({
      start: mostRecentHighlightIndex,
      size: items.length,
      circular: false,
      steps: isDown ? 1 : -1
    });

    var endIndex = !metaKeyPressed ? _index2 : isDown ? items.length - 1 : 0;

    var _changes3 = isValidIndex(state.highlightedGroupStartIndex) ? {
      highlightedGroupEndIndex: endIndex
    } : isValidIndex(mostRecentHighlightIndex) ? {
      highlightedGroupStartIndex: mostRecentHighlightIndex,
      highlightedGroupEndIndex: endIndex
    } : {
      highlightedGroupStartIndex: _index2,
      highlightedGroupEndIndex: endIndex
    };

    return omitUnchangedState(_changes3, params);
  }

  if (key === 'Home' || key === 'ArrowUp' && metaKeyPressed || key === 'End' || key === 'ArrowDown' && metaKeyPressed) {
    var _changes4 = {
      highlightedIndexes: key === 'Home' || key === 'ArrowUp' ? [0] : [items.length - 1]
    };
    return omitUnchangedState(_changes4, params);
  }

  if (key === 'ArrowDown' || key === 'ArrowUp') {
    var _isDown = key === 'ArrowDown';

    var highlightedIndexes = getNextWrappingIndexes({
      start: mostRecentHighlightIndex,
      size: items.length,
      circular: true,
      steps: _isDown ? 1 : -1
    });

    var _changes5 = _objectSpread(_objectSpread({}, getHighlightReset(defaultState)), {}, {
      highlightedIndexes
    });

    return omitUnchangedState(_changes5, params);
  }

  if (key === 'Tab') {
    var _changes6 = {
      isOpen: false
    };
    return omitUnchangedState(_changes6, params);
  }

  return {};
}
function getChangesFromToggleButtonKeyDown(_ref23) {
  var key = _ref23.key,
      defaultState = _ref23.defaultState,
      props = _ref23.props,
      getItemId = _ref23.getItemId,
      state = _ref23.state;
  var params = {
    state,
    getItemId
  };

  if (key === 'ArrowDown' || key === 'ArrowUp' || key === 'Enter' || key === 'Space') {
    var isNext = key === 'ArrowDown';
    var isPrev = key === 'ArrowUp';
    var highlights = props.type === Type.Select ? {
      highlightedIndexes: getHighlightedIndexOnOpen(props, state, isNext ? 1 : isPrev ? -1 : 0, getItemId)
    } : {};

    var changes = _objectSpread({
      isOpen: true
    }, highlights);

    return omitUnchangedState(changes, params);
  }

  if (key === 'Escape') {
    return omitUnchangedState(_objectSpread(_objectSpread({}, getHighlightReset(defaultState)), {}, {
      isOpen: false
    }), params);
  }

  return {};
}
var getChangesFromInputKeyDown = params => {
  return getChangesFromMenuKeyDown(params);
};
var modifierKeys = ['altKey', 'shiftKey', 'metaKey', 'ctrlKey'];

/**
 * Get an array of the event modifiers
 */
function getModifiers(event) {
  return modifierKeys.filter(key => event[key]);
}
/**
 * This is intended to be used to compose event handlers. They are executed in
 * order until one of them returns a truthy value.
 */

function callAllEventHandlers() {
  for (var _len = arguments.length, fns = new Array(_len), _key = 0; _key < _len; _key++) {
    fns[_key] = arguments[_key];
  }

  return function (event) {
    for (var _len2 = arguments.length, args = new Array(_len2 > 1 ? _len2 - 1 : 0), _key2 = 1; _key2 < _len2; _key2++) {
      args[_key2 - 1] = arguments[_key2];
    }

    fns.some(fn => {
      if (fn) {
        return fn(event, ...args) === true;
      }

      return false;
    });
  };
}

function bindActionCreator(actionCreator, dispatch) {
  return function () {
    return dispatch(actionCreator(...arguments));
  };
}
/**
 * Turns an object whose values are action creators, into an object with the
 * same keys, but with every function wrapped into a `dispatch` call so they may
 * be invoked directly.
 */


function bindActionCreators(actionCreators, dispatch) {
  var boundActionCreators = object();
  var creatorKeys = keys(actionCreators);

  var _iterator2 = _createForOfIteratorHelper(creatorKeys),
      _step2;

  try {
    for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
      var key = _step2.value;
      var actionCreator = assertGet(actionCreators, key);
      boundActionCreators[key] = bindActionCreator(actionCreator, dispatch);
    }
  } catch (err) {
    _iterator2.e(err);
  } finally {
    _iterator2.f();
  }

  return boundActionCreators;
}
/**
 * Create a payload for the keydown event.
 */

function createKeyDownPayload(event, key, disabled) {
  return {
    event,
    key,
    modifiers: getModifiers(event),
    disabled
  };
}
/**
 * Create a payload for the item click event.
 */

function createItemClickPayload(event, index) {
  return {
    event,
    modifiers: getModifiers(event),
    index
  };
}
/**
 * Check that the character is valid for jumpText.
 */

function isValidCharacterKey(key) {
  return /^\S$/.test(key);
}
/**
 * Scroll node into view if necessary
 * @param element - the element that should scroll into view
 * @param menuElement - the menu element of the component
 */

function scrollIntoView(element, menuElement) {
  if (!element || !menuElement) {
    return;
  }

  var actions = computeScrollIntoView(element, {
    boundary: menuElement,
    block: 'nearest',
    scrollMode: 'if-needed'
  });
  actions.forEach(_ref24 => {
    var el = _ref24.el,
        top = _ref24.top,
        left = _ref24.left;
    el.scrollTop = top;
    el.scrollLeft = left;
  });
}
/**
 * Checks whether the passed value is a valid dom node
 *
 * @param domNode - the dom node
 */

function isNode(domNode) {
  return isObject(Node) ? domNode instanceof Node : isObject(domNode) && isNumber(domNode.nodeType) && isString(domNode.nodeName);
}
/**
 * Checks for an element node like `<p>` or `<div>`.
 *
 * @param domNode - the dom node
 */

var isHTMLElement = domNode => isNode(domNode) && domNode.nodeType === Node.ELEMENT_NODE;
/**
 * Checks whether the parent contains (or is the same as) the child node.
 */

function isOrContainsNode(parent, child) {
  return parent === child || parent.contains(child);
}

/**
 * Helpers for transforming the state object.
 */
function createStateHelpers(_ref25, state) {
  var _ref25$getItemId = _ref25.getItemId,
      getItemId = _ref25$getItemId === void 0 ? defaultGetItemId : _ref25$getItemId,
      multiple = _ref25.multiple;
  return {
    addItems: itemsToAdd => _addItems(state.selectedItems, itemsToAdd, getItemId, multiple),
    addItem: itemToAdd => _addItems(state.selectedItems, [itemToAdd], getItemId, multiple),
    removeItems: itemsToRemove => _removeItems(state.selectedItems, itemsToRemove, getItemId),
    removeItem: itemToRemove => _removeItems(state.selectedItems, [itemToRemove], getItemId),
    toggleItems: itemsToToggle => _removeItems(state.selectedItems, itemsToToggle, getItemId),
    toggleItem: itemToToggle => _removeItems(state.selectedItems, [itemToToggle], getItemId)
  };
}

var multishiftReducer = (state, action, props) => {
  var changes = object();
  var defaultState = getDefaultState(props);
  var multiple = props.multiple,
      items = props.items,
      _props$getItemId = props.getItemId,
      getItemId = _props$getItemId === void 0 ? defaultGetItemId : _props$getItemId,
      _props$autoSelectOnBl = props.autoSelectOnBlur,
      autoSelectOnBlur = _props$autoSelectOnBl === void 0 ? true : _props$autoSelectOnBl,
      itemToString = props.itemToString,
      initialHighlightedIndexes = props.initialHighlightedIndexes;
  var highlightReset = getHighlightReset(defaultState);

  switch (action.type) {
    case MultishiftActionTypes.SelectItems:
    case MultishiftActionTypes.SelectItem:
      {
        var extra = action.payload.keepHighlights ? {} : highlightReset;
        changes = _objectSpread(_objectSpread({}, extra), {}, {
          selectedItems: _addItems(state.selectedItems, action.payload.items, getItemId, multiple)
        });
        break;
      }

    case MultishiftActionTypes.RemoveSelectedItems:
    case MultishiftActionTypes.RemoveSelectedItem:
      {
        var _extra = action.payload.keepHighlights ? {} : highlightReset;

        changes = _objectSpread(_objectSpread({}, _extra), {}, {
          selectedItems: _removeItems(state.selectedItems, action.payload.items, getItemId)
        });
        break;
      }

    case MultishiftActionTypes.ClearSelection:
      changes = _objectSpread(_objectSpread({}, highlightReset), {}, {
        selectedItems: []
      });
      break;

    case MultishiftActionTypes.SetHoverItemIndex:
      changes = _objectSpread(_objectSpread({}, state), {}, {
        hoveredIndex: action.payload
      });
      break;

    case MultishiftActionTypes.ItemMouseMove:
      changes = {
        hoveredIndex: action.payload
      };
      break;

    case MultishiftActionTypes.ItemMouseLeave:
      changes = state.hoveredIndex === action.payload ? {
        hoveredIndex: defaultState.hoveredIndex
      } : {};
      break;

    case MultishiftActionTypes.ItemClick:
      {
        var _action$payload = action.payload,
            modifiers = _action$payload.modifiers,
            index = _action$payload.index;
        changes = getChangesFromItemClick({
          defaultState,
          state,
          index,
          items,
          modifiers,
          props,
          getItemId
        });
        break;
      }

    case MultishiftActionTypes.InputBlur:
    case MultishiftActionTypes.ToggleButtonBlur:
    case MultishiftActionTypes.MenuBlur:
      {
        var highlightedIndexes = state.highlightedIndexes,
            selected = state.selectedItems,
            highlightedGroupStartIndex = state.highlightedGroupStartIndex,
            highlightedGroupEndIndex = state.highlightedGroupEndIndex;
        var indexes = getHighlightedIndexes({
          start: highlightedGroupStartIndex,
          end: highlightedGroupEndIndex,
          indexes: highlightedIndexes,
          items
        });

        var _extra2 = !isEmptyArray(indexes) && autoSelectOnBlur ? {
          selectedItems: _addItems(selected, indexes.map(index => assertGet(items, index)), getItemId, multiple)
        } : {};

        changes = omitUnchangedState(_objectSpread({
          isOpen: defaultState.isOpen,
          highlightedIndexes: defaultState.highlightedIndexes,
          highlightedGroupEndIndex: defaultState.highlightedGroupEndIndex,
          highlightedGroupStartIndex: defaultState.highlightedGroupStartIndex,
          hoveredIndex: defaultState.hoveredIndex,
          jumpText: defaultState.jumpText
        }, _extra2), {
          state,
          getItemId
        });
        break;
      }

    case MultishiftActionTypes.MenuSpecialKeyDown:
      {
        var _action$payload2 = action.payload,
            key = _action$payload2.key,
            _modifiers = _action$payload2.modifiers,
            disabled = _action$payload2.disabled;
        changes = getChangesFromMenuKeyDown({
          defaultState,
          items,
          key,
          modifiers: _modifiers,
          props,
          state,
          getItemId,
          disabled
        });
        break;
      }

    case MultishiftActionTypes.ToggleButtonSpecialKeyDown:
      {
        var _action$payload3 = action.payload,
            _key = _action$payload3.key,
            _modifiers2 = _action$payload3.modifiers,
            _disabled = _action$payload3.disabled;
        changes = getChangesFromToggleButtonKeyDown({
          defaultState,
          items,
          key: _key,
          modifiers: _modifiers2,
          props,
          state,
          getItemId,
          disabled: _disabled
        });
        break;
      }

    case MultishiftActionTypes.InputSpecialKeyDown:
      {
        var _action$payload4 = action.payload,
            _key2 = _action$payload4.key,
            _modifiers3 = _action$payload4.modifiers,
            _disabled2 = _action$payload4.disabled;
        changes = getChangesFromInputKeyDown({
          defaultState,
          items,
          key: _key2,
          modifiers: _modifiers3,
          props,
          state,
          getItemId,
          disabled: _disabled2
        });
        break;
      }

    case MultishiftActionTypes.MenuCharacterKeyDown:
      {
        var jumpText = "".concat(state.jumpText).concat(action.payload);

        var _indexes = getItemIndexesByJumpText({
          text: jumpText,
          highlightedIndexes: state.highlightedIndexes,
          itemToString,
          items
        });

        var extraHighlights = !isEmptyArray(_indexes) ? {
          highlightedIndexes: _indexes
        } : {};
        changes = omitUnchangedState(_objectSpread({
          jumpText
        }, extraHighlights), {
          state,
          getItemId
        });
        break;
      }

    case MultishiftActionTypes.ToggleButtonClick:
    case MultishiftActionTypes.ToggleMenu:
      {
        var _indexes2 = getHighlightedIndexOnOpen({
          defaultHighlightedIndexes: defaultState.highlightedIndexes,
          initialHighlightedIndexes: initialHighlightedIndexes,
          items
        }, state, 0, getItemId);

        changes = {
          isOpen: !state.isOpen,
          highlightedIndexes: state.isOpen ? [] : _indexes2
        };
        break;
      }

    case MultishiftActionTypes.OpenMenu:
      {
        var _highlightedIndexes = getHighlightedIndexOnOpen({
          defaultHighlightedIndexes: defaultState.highlightedIndexes,
          initialHighlightedIndexes: initialHighlightedIndexes,
          items
        }, state, 0, getItemId);

        changes = {
          isOpen: true,
          highlightedIndexes: _highlightedIndexes,
          highlightedGroupEndIndex: undefined,
          highlightedGroupStartIndex: -1
        };
        break;
      }

    case MultishiftActionTypes.CloseMenu:
      changes = {
        isOpen: false
      };
      break;

    case MultishiftActionTypes.SetHighlightedIndexes:
    case MultishiftActionTypes.SetHighlightedIndex:
      changes = {
        highlightedIndexes: action.payload
      };
      break;

    case MultishiftActionTypes.ClearHighlighted:
      changes = getHighlightReset(defaultState);
      break;

    case MultishiftActionTypes.ClearJumpText:
      changes = {
        jumpText: ''
      };
      break;

    case MultishiftActionTypes.OuterMouseUp:
    case MultishiftActionTypes.OuterTouchEnd:
      {
        var _highlightedIndexes2 = state.highlightedIndexes,
            _selected = state.selectedItems,
            _highlightedGroupEndIndex = state.highlightedGroupEndIndex,
            _highlightedGroupStartIndex = state.highlightedGroupStartIndex;

        var _indexes3 = getHighlightedIndexes({
          start: _highlightedGroupStartIndex,
          end: _highlightedGroupEndIndex,
          indexes: _highlightedIndexes2,
          items
        });

        var highlightedItems = _indexes3.map(index => assertGet(items, index));

        var _extra3 = !isEmptyArray(_indexes3) && autoSelectOnBlur ? {
          selectedItems: _addItems(_selected, highlightedItems, getItemId, multiple)
        } : {};

        changes = _objectSpread(_objectSpread({}, _extra3), {}, {
          isOpen: false
        }, highlightReset);
        break;
      }

    case MultishiftActionTypes.Reset:
      changes = defaultState;
      break;

    case MultishiftActionTypes.InputValueChange:
      changes = _objectSpread(_objectSpread({}, getHighlightReset(defaultState)), {}, {
        isOpen: true,
        inputValue: action.payload,
        jumpText: defaultState.jumpText
      });
      break;

    case MultishiftActionTypes.SetState:
      changes = _objectSpread({}, action.payload);
      break;

    default:
      warnIfInternalType(action.type, 'All internal action types need to be managed');
  }

  return [_objectSpread(_objectSpread({}, state), changes), changes];
};

var _excluded$1 = ["stateReducer"];
/**
 * Creates the reducer for managing the multishift internal state.
 */

function useMultishiftReducer(props) {
  var stateReducer = props.stateReducer,
      rest = _objectWithoutProperties(props, _excluded$1);

  var initialState = getInitialStateProps(rest);
  return useReducer((prevState, action) => {
    var _multishiftReducer = multishiftReducer(prevState, action, rest),
        _multishiftReducer2 = _slicedToArray(_multishiftReducer, 2),
        state = _multishiftReducer2[0],
        changes = _multishiftReducer2[1];

    var changeset = {
      changes,
      state,
      prevState
    };
    callChangeHandlers(rest, changeset);

    if (stateReducer) {
      return stateReducer(changeset, action, rest);
    }

    return state;
  }, initialState);
}
/**
 * Creates the ids for identifying the elements in the app.
 */

function useElementIds(props) {
  var defaultId = useId();
  return getElementIds(defaultId !== null && defaultId !== void 0 ? defaultId : '', props);
}

/**
 * Get the element references.
 */
function useElementRefs() {
  var items = useRef([]);
  var ignored = useRef([]);
  var toggleButton = useRef();
  var input = useRef();
  var menu = useRef();
  var comboBox = useRef(); // Reset the items ref nodes on each call render.

  items.current = [];
  ignored.current = [];
  return useRef({
    toggleButton,
    input,
    menu,
    comboBox,
    items,
    ignored
  }).current;
}
/**
 * A default getA11yStatusMessage function is provided that will check `items.current.length`
 * and return "No results." or if there are results but no item is highlighted,
 * "resultCount results are available, use up and down arrow keys to navigate."
 * If items are highlighted it will run `itemToString(highlightedItem)` and display
 * the value of the `highlightedItem`.
 */

var defaultGetA11yStatusMessage = _ref => {
  var items = _ref.items,
      _ref$state = _ref.state,
      selectedItems = _ref$state.selectedItems,
      isOpen = _ref$state.isOpen,
      _ref$itemsToString = _ref.itemsToString,
      itemsToString = _ref$itemsToString === void 0 ? defaultItemsToString : _ref$itemsToString;

  if (!isEmptyArray(selectedItems)) {
    return "".concat(itemsToString(selectedItems), " has been selected.");
  }

  if (isEmptyArray(items)) {
    return '';
  }

  var resultCount = items.length;

  if (isOpen) {
    if (resultCount === 0) {
      return 'No results are available';
    }

    return "".concat(resultCount, " result").concat(resultCount === 1 ? ' is' : 's are', " available, use up and down arrow keys to navigate. Press Enter key to select.");
  }

  return '';
};

function useSetA11y(props) {
  var state = props.state,
      items = props.items,
      _props$itemsToString = props.itemsToString,
      itemsToString = _props$itemsToString === void 0 ? defaultItemsToString : _props$itemsToString,
      _props$getA11yStatusM = props.getA11yStatusMessage,
      getA11yStatusMessage = _props$getA11yStatusM === void 0 ? defaultGetA11yStatusMessage : _props$getA11yStatusM,
      _props$customA11yStat = props.customA11yStatusMessage,
      customA11yStatusMessage = _props$customA11yStat === void 0 ? '' : _props$customA11yStat;
  var automaticMessage = getA11yStatusMessage({
    state,
    items,
    itemsToString
  }); // Sets a11y status message on changes to relevant state values.

  useEffectOnUpdate(() => {
    setStatus(automaticMessage);
  }, [state.isOpen, state.selectedItems]); // Sets a11y status message on changes in customA11yStatusMessage

  useEffect(() => {
    if (customA11yStatusMessage) {
      setStatus(customA11yStatusMessage);
    }
  }, [customA11yStatusMessage]);
}
/**
 * This is a hook that listens for events mouse and touch events.
 *
 * When something does occur outside of the registered elements it will dispatch
 * the relevant action.
 */

function useOuterEventListener(refs, state, _ref2) {
  var outerMouseUp = _ref2.outerMouseUp,
      outerTouchEnd = _ref2.outerTouchEnd;
  var context = useRef({
    isMouseDown: false,
    isTouchMove: false,
    lastBlurred: undefined
  });
  var isOpen = useRef(state.isOpen);
  isOpen.current = state.isOpen;

  var targetWithinMultishift = function targetWithinMultishift(target) {
    var checkActiveElement = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
    return [refs.comboBox.current, refs.menu.current, refs.toggleButton.current, refs.input.current, ...refs.ignored.current, ...refs.items.current].some(node => {
      return node && (isOrContainsNode(node, target) || checkActiveElement && isOrContainsNode(node, window.document.activeElement));
    });
  };

  useEffectOnce(() => {
    // Borrowed from `downshift`
    // context.current.isMouseDown helps us track whether the mouse is currently held down.
    // This is useful when the user clicks on an item in the list, but holds the mouse
    // down long enough for the list to disappear (because the blur event fires on the input)
    // context.current.isMouseDown is used in the blur handler on the input to determine whether the blur event should
    // trigger hiding the menu.
    var onMouseDown = () => {
      context.current.isMouseDown = true;
    };

    var onMouseUp = event => {
      context.current.isMouseDown = false; // if the target element or the activeElement is within a multishift node
      // then we don't want to reset multishift

      var contextWithinMultishift = targetWithinMultishift(event.target);

      if (!contextWithinMultishift && isOpen.current) {
        outerMouseUp();
      }
    }; // Borrowed from `downshift`
    // Touching an element in iOS gives focus and hover states, but touching out of
    // the element will remove hover, and persist the focus state, resulting in the
    // blur event not being triggered.
    // context.current.isTouchMove helps us track whether the user is tapping or swiping on a touch screen.
    // If the user taps outside of Multishift, the component should be reset,
    // but not if the user is swiping


    var onTouchStart = () => {
      context.current.isTouchMove = false;
    };

    var onTouchMove = () => {
      context.current.isTouchMove = true;
    };

    var onTouchEnd = event => {
      var contextWithinMultishift = targetWithinMultishift(event.target, false);

      if (!context.current.isTouchMove && !contextWithinMultishift && isOpen.current) {
        outerTouchEnd();
      }
    };

    window.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mouseup', onMouseUp);
    window.addEventListener('touchstart', onTouchStart);
    window.addEventListener('touchmove', onTouchMove);
    window.addEventListener('touchend', onTouchEnd);
    return () => {
      window.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
    };
  });
  return context;
}
/**
 * A hook for managing multiple timeouts.
 *
 * @remarks
 *
 * All timeouts are automatically cleared when un-mounting.
 */

function useTimeouts() {
  var timeoutIds = useRef([]);

  var setHookTimeout = function setHookTimeout(fn) {
    var time = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 1;
    var id = setTimeout(() => {
      timeoutIds.current = timeoutIds.current.filter(timeoutId => timeoutId !== id);
      fn();
    }, time);
    timeoutIds.current.push(id);
  };

  var clearHookTimeouts = () => {
    timeoutIds.current.forEach(id => {
      clearTimeout(id);
    });
    timeoutIds.current = [];
  }; // Clear the timeouts on dismount


  useEffectOnce(() => clearHookTimeouts);
  return [setHookTimeout, clearHookTimeouts];
}
/**
 * React effect hook that ignores the first invocation (e.g. on mount).
 *
 * @remarks
 *
 * The signature is exactly the same as the useEffect hook.
 *
 * ```tsx
 * import React from 'react'
 * import { useEffectOnUpdate } from 'react-use';
 *
 * const Demo = () => {
 *   const [count, setCount] = React.useState(0);
 *
 *   React.useEffect(() => {
 *     const interval = setInterval(() => {
 *       setCount(count => count + 1)
 *     }, 1000)
 *
 *     return () => {
 *       clearInterval(interval)
 *     }
 *   }, [])
 *
 *   useEffectOnUpdate(() => {
 *     log('count', count) // will only show 1 and beyond
 *
 *     return () => { // *OPTIONAL*
 *       // do something on unmount
 *     }
 *   }) // you can include deps array if necessary
 *
 *   return <div>Count: {count}</div>
 * };
 * ```
 */

function useEffectOnUpdate(effect, dependencies) {
  var isInitialMount = useRef(true);
  useShallowCompareEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else {
      return effect();
    }
  }, [dependencies]);
}

var _excluded = ["refKey", "ref"],
    _excluded2 = ["onKeyDown", "onBlur", "onChange", "onInput", "refKey", "ref"],
    _excluded3 = ["onKeyDown", "onBlur", "refKey", "ref"],
    _excluded4 = ["onClick", "onKeyDown", "onBlur", "refKey", "ref"],
    _excluded5 = ["item", "index", "refKey", "ref", "onMouseMove", "onMouseLeave", "onClick"],
    _excluded6 = ["refKey", "ref", "onMouseMove", "onFocus"],
    _excluded7 = ["refKey", "ref"],
    _excluded8 = ["onClick", "item"];
/**
 * Multishift is a hook that provides all the necessary tools for building
 * accessible dropdown components.
 *
 * @remarks
 *
 * It supports `select` and `autocomplete` drop down experiences with built in
 * support for multi-selection.
 *
 * The library borrows (and steals) heavily from `downshift` but also adds some
 * features which are really important for the `remirror` project.
 *
 * - Focus on typescript support
 * - No React Native support
 * - Multi selection support
 * - Render with **only** menu support (fully controlled)
 *
 * Eventually some of the code will be contributed back to the downshift
 * library.
 */

var useMultishift = props => {
  var type = props.type,
      customA11yStatusMessage = props.customA11yStatusMessage,
      getA11yStatusMessage = props.getA11yStatusMessage,
      items = props.items,
      _props$getItemId = props.getItemId,
      getItemId = _props$getItemId === void 0 ? defaultGetItemId : _props$getItemId,
      multiple = props.multiple;

  var _useMultishiftReducer = useMultishiftReducer(props),
      _useMultishiftReducer2 = _slicedToArray(_useMultishiftReducer, 2),
      state = _useMultishiftReducer2[0],
      dispatch = _useMultishiftReducer2[1];

  var actions = useMemo(() => bindActionCreators(Actions, dispatch), [dispatch]);
  useSetA11y({
    state,
    items,
    customA11yStatusMessage,
    getA11yStatusMessage
  });
  var stateProps = getState(state, props);
  var highlightedIndexes = stateProps.highlightedIndexes,
      highlightedGroupEndIndex = stateProps.highlightedGroupEndIndex,
      highlightedGroupStartIndex = stateProps.highlightedGroupStartIndex,
      selectedItems = stateProps.selectedItems,
      hoveredIndex = stateProps.hoveredIndex,
      inputValue = stateProps.inputValue,
      isOpen = stateProps.isOpen,
      jumpText = stateProps.jumpText;

  var _useElementIds = useElementIds(props),
      getItemA11yId = _useElementIds.getItemA11yId,
      labelId = _useElementIds.labelId,
      menuId = _useElementIds.menuId,
      toggleButtonId = _useElementIds.toggleButtonId,
      inputId = _useElementIds.inputId; // Refs


  var refs = useElementRefs();
  var shouldScroll = useRef(true);
  var clearJumpText = debounce(500, actions.clearJumpText);
  var disabled = useRef([]);
  disabled.current = [];
  var contextRef = useOuterEventListener(refs, stateProps, {
    outerMouseUp: actions.outerMouseUp,
    outerTouchEnd: actions.outerTouchEnd
  });

  var _useTimeouts = useTimeouts(),
      _useTimeouts2 = _slicedToArray(_useTimeouts, 2),
      setInternalTimeout = _useTimeouts2[0],
      clearTimeouts = _useTimeouts2[1]; // The most recently highlighted index.


  var mostRecentHighlightedIndex = getMostRecentHighlightIndex({
    highlightedGroupEndIndex,
    highlightedGroupStartIndex,
    highlightedIndexes
  });
  useEffectOnce(() => clearTimeouts());
  useEffectOnUpdate(() => {
    if (!jumpText) {
      return;
    }

    clearJumpText();
  }, [jumpText]); // Focus on the menu if the starting position isOpen.

  useEffectOnce(() => {
    if (isOpen) {
      if (type === Type.ComboBox && refs.input.current) {
        refs.input.current.focus();
      } else if (refs.menu.current && type !== Type.ControlledMenu) {
        refs.menu.current.focus();
      }
    }
  }); // Every time `isOpen` changes update the focus to the input / toggleButton
  // depending on the type of editor.

  useEffectOnUpdate(() => {
    if (isOpen) {
      if (type === Type.ComboBox && refs.input.current) {
        refs.input.current.focus();
      } else if (refs.menu.current && type !== Type.ControlledMenu) {
        refs.menu.current.focus();
      }
    } else if (document.activeElement === refs.menu.current) {
      if (type === Type.ComboBox && refs.input.current) {
        refs.input.current.focus();
      } else if (refs.toggleButton.current) {
        refs.toggleButton.current.focus();
      }
    }
  }, [isOpen]); // Scroll on highlighted item if change comes from keyboard.

  useEffect(() => {
    if (!isValidIndex(mostRecentHighlightedIndex) || !isOpen || isEmptyArray(refs.items.current)) {
      return;
    }

    if (shouldScroll.current === false) {
      shouldScroll.current = true;
    } else if (refs.menu.current) {
      scrollIntoView(refs.items.current[mostRecentHighlightedIndex], refs.menu.current);
    }
  }, [isOpen, mostRecentHighlightedIndex, refs.items, refs.menu]);
  var itemHighlightedAtIndex = useCallback(index => {
    var isHovered = index === hoveredIndex;
    return checkItemHighlighted(index, {
      start: highlightedGroupStartIndex,
      end: highlightedGroupEndIndex,
      indexes: highlightedIndexes
    }) || (multiple ? isHovered : !isValidIndex(mostRecentHighlightedIndex) && isHovered);
  }, [highlightedGroupEndIndex, highlightedGroupStartIndex, highlightedIndexes, hoveredIndex, mostRecentHighlightedIndex, multiple]);
  var indexIsHovered = useCallback(index => {
    return index === hoveredIndex;
  }, [hoveredIndex]);
  var itemIsHovered = useCallback(item => {
    return item === items[hoveredIndex];
  }, [hoveredIndex, items]);
  var indexIsSelected = useCallback(index => checkItemHighlighted(index, {
    start: highlightedGroupStartIndex,
    end: highlightedGroupEndIndex,
    indexes: highlightedIndexes
  }), [highlightedGroupEndIndex, highlightedGroupStartIndex, highlightedIndexes]);
  var itemIsSelected = useCallback(item => selectedItems.map(getItemId).includes(getItemId(item)), [getItemId, selectedItems]);
  var indexOfItem = useCallback(item => items.map(getItemId).indexOf(getItemId(item)), [getItemId, items]);
  var getComboBoxProps = useCallback(function () {
    var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {
      refKey: 'ref'
    };

    var _options$refKey = options.refKey,
        refKey = _options$refKey === void 0 ? 'ref' : _options$refKey,
        ref = options.ref,
        rest = _objectWithoutProperties(options, _excluded);

    if (type !== Type.ComboBox) {
      throw new Error('`getComboBoxProps` is only available for the autocomplete dropdown');
    }

    var extra = isUndefined(rest['aria-label']) ? {
      'aria-labelledby': labelId
    } : {};
    return _objectSpread(_objectSpread({
      [refKey]: composeRefs(ref, refs.comboBox),
      role: 'combobox',
      'aria-expanded': isOpen,
      'aria-haspopup': 'listbox',
      'aria-owns': isOpen ? menuId : null
    }, extra), rest);
  }, [isOpen, labelId, menuId, refs.comboBox, type]);
  var getInputProps = useCallback(function () {
    var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {
      refKey: 'ref'
    };

    var onKeyDown = options.onKeyDown,
        onBlur = options.onBlur,
        onChange = options.onChange,
        onInput = options.onInput,
        _options$refKey2 = options.refKey,
        refKey = _options$refKey2 === void 0 ? 'ref' : _options$refKey2,
        ref = options.ref,
        rest = _objectWithoutProperties(options, _excluded2);

    if (type !== Type.ComboBox) {
      throw new Error('`getInputProps` is only available for the `autocomplete` dropdown');
    }

    var activeDescendant = isValidIndex(mostRecentHighlightedIndex) ? {
      'aria-activedescendant': getItemA11yId(mostRecentHighlightedIndex)
    } : {};
    var eventHandlers = {
      onChange: callAllEventHandlers(onChange, onInput, event => {
        actions.inputValueChange(event.target.value);
      }),
      onKeyDown: callAllEventHandlers(onKeyDown, event => {
        var key = getKeyName(event);

        if (includes(SPECIAL_INPUT_KEYS, key)) {
          actions.inputSpecialKeyDown(createKeyDownPayload(event, key, disabled.current));
          event.preventDefault();
        }
      }),
      // TODO test this blur handler.
      onBlur: callAllEventHandlers(onBlur, () => {
        setInternalTimeout(() => {
          var multishiftActive = isHTMLElement(document.activeElement) && refs.comboBox.current && refs.comboBox.current.contains(document.activeElement);

          if (!contextRef.current.isMouseDown && !multishiftActive) {
            actions.inputBlur();
          }
        });
      })
    };

    if (rest.disabled) {
      eventHandlers = object();
    }

    return _objectSpread(_objectSpread(_objectSpread({
      [refKey]: composeRefs(ref, refs.input),
      'aria-autocomplete': 'list'
    }, activeDescendant), {}, {
      'aria-controls': isOpen ? menuId : null,
      'aria-labelledby': labelId,
      // https://developer.mozilla.org/en-US/docs/Web/Security/Securing_your_site/Turning_off_form_autocompletion
      // revert back since autocomplete="nope" is ignored on latest Chrome and
      // Opera
      autoComplete: 'off',
      value: inputValue,
      id: inputId
    }, eventHandlers), rest);
  }, [actions, contextRef, getItemA11yId, inputId, inputValue, isOpen, labelId, menuId, mostRecentHighlightedIndex, refs, setInternalTimeout, type]);
  var getMenuProps = useCallback(function () {
    var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {
      refKey: 'ref'
    };

    var onKeyDown = options.onKeyDown,
        onBlur = options.onBlur,
        _options$refKey3 = options.refKey,
        refKey = _options$refKey3 === void 0 ? 'ref' : _options$refKey3,
        ref = options.ref,
        rest = _objectWithoutProperties(options, _excluded3);

    var multi = multiple ? {
      'aria-multiselectable': multiple
    } : {};
    var activeDescendant = !isEmptyArray(highlightedIndexes) ? {
      'aria-activedescendant': getItemA11yId(mostRecentHighlightedIndex)
    } : {};
    var eventHandlers = {
      onKeyDown: callAllEventHandlers(onKeyDown, event => {
        var key = getKeyName(event);

        if (includes(SPECIAL_MENU_KEYS, key)) {
          actions.menuSpecialKeyDown(createKeyDownPayload(event, key, disabled.current));

          if (key !== 'Tab') {
            event.preventDefault();
          }
        } else if (isValidCharacterKey(key)) {
          actions.menuCharacterKeyDown(key);
        }
      }),
      onBlur: callAllEventHandlers(onBlur, event => {
        var blurTarget = event.target;
        setInternalTimeout(() => {
          if (!contextRef.current.isMouseDown && (isNullOrUndefined(document.activeElement) || ![refs.comboBox.current, refs.input.current, refs.toggleButton.current, ...refs.items.current, ...refs.ignored.current].some(node => node && isOrContainsNode(node, document.activeElement)) && document.activeElement !== blurTarget)) {
            actions.menuBlur();
          }
        });
      })
    };

    if (rest.disabled) {
      eventHandlers = object();
    }

    return _objectSpread(_objectSpread(_objectSpread(_objectSpread({
      [refKey]: composeRefs(ref, refs.menu),
      id: menuId,
      role: type === Type.ControlledMenu ? 'menu' : 'listbox',
      'aria-labelledby': labelId,
      tabIndex: -1
    }, multi), activeDescendant), eventHandlers), rest);
  }, [actions, contextRef, getItemA11yId, highlightedIndexes, labelId, menuId, mostRecentHighlightedIndex, multiple, refs, setInternalTimeout, type]);
  var getToggleButtonProps = useCallback(function () {
    var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {
      refKey: 'ref'
    };

    var onClick = options.onClick,
        onKeyDown = options.onKeyDown,
        onBlur = options.onBlur,
        _options$refKey4 = options.refKey,
        refKey = _options$refKey4 === void 0 ? 'ref' : _options$refKey4,
        ref = options.ref,
        rest = _objectWithoutProperties(options, _excluded4);

    if (type === Type.ControlledMenu) {
      throw new Error('The toggle button props should not be used for the controlled menu');
    }

    var isInternalEvent = event => [refs.input.current, refs.menu.current, ...refs.items.current].some(node => node && isOrContainsNode(node, event.target));

    var eventHandlers = {
      onClick: callAllEventHandlers(onClick, () => actions.toggleButtonClick()),
      onKeyDown: callAllEventHandlers(onKeyDown, event => {
        var key = getKeyName(event);

        if (isInternalEvent(event)) {
          return;
        }

        if (includes(SPECIAL_TOGGLE_BUTTON_KEYS, key)) {
          actions.toggleButtonSpecialKeyDown(createKeyDownPayload(event, key, disabled.current));
          event.preventDefault();
        }
      }),
      onBlur: callAllEventHandlers(onBlur, event => {
        if (isInternalEvent(event)) {
          return;
        }

        var blurTarget = event.target; // Save blur target for comparison with activeElement later
        // Need setTimeout, so that when the user presses Tab, the activeElement
        // is the next focused element, not body element

        setInternalTimeout(() => {
          if (!contextRef.current.isMouseDown && (isNullOrUndefined(document.activeElement) || document.activeElement.id !== menuId) && document.activeElement !== blurTarget // Do nothing if we refocus the same element again (to solve issue in Safari on iOS)
          ) {
            actions.toggleButtonBlur();
          }
        });
      })
    };

    if (rest.disabled) {
      eventHandlers = object();
    }

    var extra = type === Type.Select ? {
      'aria-expanded': isOpen
    } : {};
    var ariaLabel = isUndefined(rest['aria-label']) ? {
      'aria-labelledby': "".concat(labelId, " ").concat(toggleButtonId)
    } : {};
    return _objectSpread(_objectSpread(_objectSpread(_objectSpread({
      [refKey]: composeRefs(ref, refs.toggleButton),
      type: 'button',
      role: 'button',
      id: toggleButtonId,
      'aria-haspopup': type === Type.ComboBox ? true : 'listbox'
    }, ariaLabel), extra), eventHandlers), rest);
  }, [actions, contextRef, isOpen, labelId, menuId, refs, setInternalTimeout, toggleButtonId, type]);
  var getItemProps = useCallback(options => {
    var item = options.item,
        index = options.index,
        _options$refKey5 = options.refKey,
        refKey = _options$refKey5 === void 0 ? 'ref' : _options$refKey5,
        ref = options.ref,
        onMouseMove = options.onMouseMove,
        onMouseLeave = options.onMouseLeave,
        onClick = options.onClick,
        rest = _objectWithoutProperties(options, _excluded5);

    var itemIndex = getItemIndex(index, item, items);

    if (!isValidIndex(itemIndex)) {
      throw new Error('Pass either item or item index in getItemProps!');
    }

    var eventHandlers = {
      onMouseMove: callAllEventHandlers(onMouseMove, () => actions.itemMouseMove(itemIndex)),
      onMouseLeave: callAllEventHandlers(onMouseLeave, () => actions.itemMouseLeave(itemIndex)),
      onClick: callAllEventHandlers(onClick, event => {
        event.preventDefault();
        actions.itemClick(createItemClickPayload(event, itemIndex));
      })
    };

    if (rest.disabled) {
      disabled.current.push(itemIndex);
      eventHandlers = object();
    }

    return _objectSpread(_objectSpread({
      [refKey]: composeRefs(ref, itemNode => {
        if (itemNode) {
          refs.items.current.push(itemNode);
        }
      }),
      role: type === Type.ControlledMenu ? multiple ? 'menuitemcheckbox' : 'menuitemradio' : 'option',
      'aria-current': index === hoveredIndex || index === mostRecentHighlightedIndex,
      'aria-selected': itemHighlightedAtIndex(index) && !rest.disabled,
      id: getItemA11yId(itemIndex)
    }, eventHandlers), rest);
  }, [actions, getItemA11yId, hoveredIndex, itemHighlightedAtIndex, items, mostRecentHighlightedIndex, multiple, refs.items, type]);
  var getIgnoredElementProps = useCallback(function () {
    var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {
      refKey: 'ref'
    };

    var _options$refKey6 = options.refKey,
        refKey = _options$refKey6 === void 0 ? 'ref' : _options$refKey6,
        ref = options.ref;
        options.onMouseMove;
        options.onFocus;
        var rest = _objectWithoutProperties(options, _excluded6);

    var eventHandlers = object();

    if (rest.disabled) {
      eventHandlers = object();
    }

    return _objectSpread(_objectSpread({
      [refKey]: composeRefs(ref, node => {
        if (node && !rest.disabled) {
          refs.ignored.current.push(node);
        }
      })
    }, eventHandlers), rest);
  }, [refs.ignored]);
  var getLabelProps = useCallback(function () {
    var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {
      refKey: 'ref'
    };

    var _options$refKey7 = options.refKey,
        refKey = _options$refKey7 === void 0 ? 'ref' : _options$refKey7,
        ref = options.ref,
        rest = _objectWithoutProperties(options, _excluded7);

    return _objectSpread({
      [refKey]: composeRefs(ref, node => {
        if (node) {
          refs.ignored.current.push(node);
        }
      }),
      id: labelId,
      htmlFor: type === 'combobox' && refs.input.current ? inputId : menuId
    }, rest);
  }, [inputId, labelId, menuId, refs, type]);
  var getRemoveButtonProps = useCallback(options => {
    var onClick = options.onClick,
        item = options.item,
        rest = _objectWithoutProperties(options, _excluded8);

    var eventHandlers = {
      onClick: callAllEventHandlers(onClick, () => {
        actions.removeSelectedItem(item);
      })
    };

    if (rest.disabled) {
      eventHandlers = object();
    }

    return _objectSpread(_objectSpread({}, eventHandlers), {}, {
      role: 'button'
    }, rest);
  }, [actions]);
  var focusHelpers = useMemo(() => ({
    focusInput: () => {
      if (type !== Type.ComboBox) {
        throw new Error("The input element cannot be focused for this type of dropdown: ".concat(type));
      }

      if (refs.input.current) {
        refs.input.current.focus();
      }
    },
    focusToggleButton: () => {
      if (refs.toggleButton.current) {
        refs.toggleButton.current.focus();
      }
    },
    focusMenu: () => {
      if (refs.menu.current) {
        refs.menu.current.focus();
      }
    },
    focusMenuItem: index => {
      if (refs.items.current[index]) {
        var _refs$items$current$i;

        (_refs$items$current$i = refs.items.current[index]) === null || _refs$items$current$i === void 0 ? void 0 : _refs$items$current$i.focus();
      }
    }
  }), [refs.input, refs.items, refs.menu, refs.toggleButton, type]);
  var stateHelpers = createStateHelpers(props, state);
  return _objectSpread(_objectSpread({
    // State
    highlightedIndexes,
    highlightedGroupEndIndex,
    highlightedGroupStartIndex,
    selectedItems,
    hoveredIndex,
    inputValue,
    isOpen,
    jumpText
  }, stateHelpers), {}, {
    // Data
    mostRecentHighlightedIndex,
    // Helpers
    itemHighlightedAtIndex,
    indexIsHovered,
    itemIsHovered,
    indexIsSelected,
    itemIsSelected,
    indexOfItem,
    // Actions
    dispatch,
    clearSelection: actions.clearSelection,
    selectItems: actions.selectItems,
    selectItem: actions.selectItem,
    clearHighlighted: actions.clearHighlighted,
    inputValueChange: actions.inputValueChange,
    removeSelectedItem: actions.removeSelectedItem,
    removeSelectedItems: actions.removeSelectedItems,
    setHoverItemIndex: actions.setHoverItemIndex,
    toggleMenu: actions.toggleMenu,
    closeMenu: actions.closeMenu,
    openMenu: actions.openMenu,
    setHighlightedIndexes: actions.setHighlightedIndexes,
    setHighlightedIndex: actions.setHighlightedIndex,
    reset: actions.reset,
    setState: actions.setState,
    clearInputValue: actions.clearInputValue,
    clearJumpText: actions.clearJumpText,
    // Props Getters
    getMenuProps,
    getComboBoxProps,
    getToggleButtonProps,
    getItemProps,
    getInputProps,
    getRemoveButtonProps,
    getLabelProps,
    getIgnoredElementProps
  }, focusHelpers);
};

export { Actions, MultishiftActionTypes, SPECIAL_INPUT_KEYS, SPECIAL_KEYS, SPECIAL_MENU_KEYS, SPECIAL_TOGGLE_BUTTON_KEYS, Type, useMultishift };
